import React, { useState } from 'react';
import styled from 'styled-components';
import { Well } from '@zendeskgarden/react-notifications';

const HelpContainer = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const SectionTitle = styled.h2`
  color: #2f3941;
  margin-bottom: 24px;
  font-size: 24px;
  font-weight: 600;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ContactSection = styled(Well)`
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid #e9ebed;
  border-radius: 8px;
`;

const ContactTitle = styled.h3`
  color: #2f3941;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const ContactItem = styled.div`
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ContactLabel = styled.div`
  color: #68737d;
  font-size: 14px;
  margin-bottom: 4px;
`;

const ContactValue = styled.div`
  color: #2f3941;
  font-size: 16px;
  font-weight: 500;
`;

const FAQSection = styled.div`
  margin-bottom: 32px;
`;

const FAQItem = styled.div`
  margin-bottom: 16px;
  border: 1px solid #e9ebed;
  border-radius: 8px;
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 16px 20px;
  background: white;
  border: none;
  text-align: left;
  font-size: 16px;
  font-weight: 500;
  color: #2f3941;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding: 0 20px 16px 20px;
  color: #68737d;
  font-size: 14px;
  line-height: 1.6;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ChevronIcon = styled.span<{ isOpen: boolean }>`
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const HelpSupport: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "To reset your password, go to your Profile page and click on the 'Password & Security' tab. Then click 'Change Password' and follow the instructions."
    },
    {
      question: "How do I manage user accounts?",
      answer: "As an admin, you can manage user accounts by navigating to the 'User Management' section from the sidebar. Here you can view, edit, activate, or deactivate user accounts."
    },
    {
      question: "How do I view system analytics?",
      answer: "System analytics are displayed on the main Admin Dashboard. You can see total users, cases analyzed, pending reviews, and accuracy rates."
    },
    {
      question: "How do I export data?",
      answer: "Data export functionality is available in each section. Look for the 'Export' button in the top-right corner of data tables."
    },
    {
      question: "How do I configure system settings?",
      answer: "System settings can be configured by navigating to the 'Settings' section from the sidebar. Here you can modify global configurations and preferences."
    },
    {
      question: "What browsers are supported?",
      answer: "DermAI supports all modern browsers including Chrome, Firefox, Safari, and Edge. We recommend using the latest version for the best experience."
    },
    {
      question: "How do I add new users to the system?",
      answer: "To add new users, go to User Management and click the 'Add User' button. Fill in the required information and assign appropriate roles."
    },
    {
      question: "How do I monitor system performance?",
      answer: "System performance metrics are available on the dashboard. For detailed analytics, check the Reports section or contact support for advanced monitoring tools."
    }
  ];

  return (
    <HelpContainer>
      <SectionTitle>Help & Support</SectionTitle>
      
      <ContactSection>
        <ContactTitle>Contact Information</ContactTitle>
        <ContactInfo>
          <ContactItem>
            <ContactLabel>Email Support</ContactLabel>
            <ContactValue>support@dermai.com</ContactValue>
          </ContactItem>
          <ContactItem>
            <ContactLabel>Phone Support</ContactLabel>
            <ContactValue>+1 (555) 123-4567</ContactValue>
          </ContactItem>
          <ContactItem>
            <ContactLabel>Business Hours</ContactLabel>
            <ContactValue>Mon-Fri 9AM-6PM EST</ContactValue>
          </ContactItem>
          <ContactItem>
            <ContactLabel>Emergency Support</ContactLabel>
            <ContactValue>Available 24/7</ContactValue>
          </ContactItem>
        </ContactInfo>
      </ContactSection>

      <FAQSection>
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion onClick={() => toggleFAQ(index)}>
              {faq.question}
              <ChevronIcon isOpen={openFAQ === index}>
                ▼
              </ChevronIcon>
            </FAQQuestion>
            <FAQAnswer isOpen={openFAQ === index}>
              {faq.answer}
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQSection>
    </HelpContainer>
  );
};

export default HelpSupport;
